import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import type { ReactNode, RefObject } from "react";
import {
  useGridApiContext,
  useGridSelector,
  gridColumnDefinitionsSelector,
  gridColumnVisibilityModelSelector,
  gridPinnedColumnsSelector,
} from "@mui/x-data-grid-premium";
import {
  Autocomplete,
  autocompleteClasses,
  Box,
  IconButton,
  Popover,
  PopperProps,
  TextField,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import { ManageSearch as ManageSearchIcon } from "@mui/icons-material";
import "./JumpToColumn.css";
import { registerJumpToColumnTarget } from "./jumpToColumnRegistry";
import {
  buildJumpToColumnOptions,
  filterJumpToColumnOptions,
  JumpToColumnOption,
} from "./jumpToColumnOptions";

const HIGHLIGHT_CLASS = "sdg-jump-to-column-highlight";
// Matches the fade-out animation in JumpToColumn.css
const HIGHLIGHT_MS = 1500;
// ~1s of frames to wait for a just-shown column to reach the grid state and
// for its (virtualized) header cell to render after the scroll
const MAX_WAIT_FRAMES = 60;
// scrollToIndexes keeps reporting a scroll for a column wider than the
// viewport, so stop retrying after a few frames
const MAX_SCROLL_ATTEMPTS = 3;

const InlinePopperRoot = styled("div")({
  [`& .${autocompleteClasses.paper}`]: {
    boxShadow: "none",
    margin: 0,
  },
  [`& .${autocompleteClasses.listbox}`]: {
    maxHeight: 320,
  },
});

// Renders the Autocomplete list inline under the input, inside the Popover,
// instead of in a second floating popper
function InlinePopper({ className, style, role, children }: PopperProps) {
  return (
    <InlinePopperRoot className={className} style={style} role={role}>
      {children as ReactNode}
    </InlinePopperRoot>
  );
}

interface JumpToColumnProps {
  // Element that counts as "inside this grid" when routing Ctrl+Shift+F
  // between grids; defaults to the grid root
  rootRef?: RefObject<HTMLElement | null>;
  // Shows a hidden column. ServerDataGrid controls columnVisibilityModel, so
  // apiRef.setColumnVisibility alone would be overridden by the prop there.
  onShowColumn?: (field: string) => void;
}

/**
 * Toolbar button + Ctrl+Shift+F picker that scrolls a column into view
 * (showing it first if hidden) and flashes its header. Column virtualization
 * keeps off-screen headers out of the DOM, so the browser's Ctrl+F can't
 * find them.
 */
export function JumpToColumn({ rootRef, onShowColumn }: JumpToColumnProps) {
  const apiRef = useGridApiContext();
  const columns = useGridSelector(apiRef, gridColumnDefinitionsSelector);
  const columnVisibilityModel = useGridSelector(
    apiRef,
    gridColumnVisibilityModelSelector
  );
  const pinnedColumns = useGridSelector(apiRef, gridPinnedColumnsSelector);

  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const frameRef = useRef<number | null>(null);
  const highlightRef = useRef<{
    element: HTMLElement;
    timer: ReturnType<typeof setTimeout>;
  } | null>(null);

  const options = useMemo(
    () =>
      buildJumpToColumnOptions(columns, columnVisibilityModel, [
        ...(pinnedColumns.left ?? []),
        ...(pinnedColumns.right ?? []),
      ]),
    [columns, columnVisibilityModel, pinnedColumns]
  );

  // Ctrl+Shift+F goes through the shared registry so only one grid's picker
  // opens when several grids are on the page
  useEffect(
    () =>
      registerJumpToColumnTarget({
        getRoot: () =>
          rootRef?.current ?? apiRef.current?.rootElementRef?.current ?? null,
        open: () => {
          // Bring the toolbar on screen so the picker isn't anchored off-screen
          buttonRef.current?.scrollIntoView?.({ block: "nearest" });
          setOpen(true);
        },
      }),
    [apiRef, rootRef]
  );

  const cancelReveal = useCallback(() => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
  }, []);

  const clearHighlight = useCallback(() => {
    const current = highlightRef.current;
    if (!current) return;
    clearTimeout(current.timer);
    current.element.classList.remove(HIGHLIGHT_CLASS);
    highlightRef.current = null;
  }, []);

  useEffect(
    () => () => {
      cancelReveal();
      clearHighlight();
    },
    [cancelReveal, clearHighlight]
  );

  const flashHeader = (element: HTMLElement) => {
    clearHighlight();
    // Force a reflow so the animation restarts on a header that just flashed
    element.classList.remove(HIGHLIGHT_CLASS);
    void element.offsetWidth;
    element.classList.add(HIGHLIGHT_CLASS);
    highlightRef.current = {
      element,
      timer: setTimeout(clearHighlight, HIGHLIGHT_MS),
    };
  };

  const revealColumn = (option: JumpToColumnOption) => {
    cancelReveal();
    const { field } = option;
    if (option.hidden) {
      if (onShowColumn) onShowColumn(field);
      else apiRef.current.setColumnVisibility(field, true);
    }

    let frames = 0;
    let scrollAttempts = 0;
    // A just-shown column gets one extra frame after it appears in grid state
    // so its width reaches the DOM before scrolling
    let ready = !option.hidden;

    const step = () => {
      frameRef.current = null;
      const api = apiRef.current;
      if (!api || ++frames > MAX_WAIT_FRAMES) return;
      const next = () => {
        frameRef.current = requestAnimationFrame(step);
      };

      const colIndex = api.getColumnIndex(field);
      if (colIndex < 0 || !ready) {
        if (colIndex >= 0) ready = true;
        return next();
      }

      // Pinned columns are always on screen; scrolling to one would only move
      // the unpinned area. scrollToIndexes returns true when it had to move.
      if (
        !api.isColumnPinned(field) &&
        scrollAttempts < MAX_SCROLL_ATTEMPTS &&
        api.scrollToIndexes({ colIndex })
      ) {
        scrollAttempts++;
        return next();
      }

      // Virtualized header cells render after the scroll event
      const header = api.getColumnHeaderElement(field);
      if (!header) return next();
      flashHeader(header);
      api.setColumnHeaderFocus(field);
    };

    frameRef.current = requestAnimationFrame(step);
  };

  const handleSelect = (option: JumpToColumnOption) => {
    setOpen(false);
    revealColumn(option);
  };

  return (
    <>
      <Tooltip title="Jump to column (Ctrl+Shift+F)">
        <IconButton
          ref={buttonRef}
          size="small"
          aria-label="Jump to column"
          onClick={() => setOpen(true)}
        >
          <ManageSearchIcon />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={buttonRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { width: 360, p: 1.5 } } }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Jump to column
        </Typography>
        <Autocomplete
          open
          autoHighlight
          fullWidth
          forcePopupIcon={false}
          options={options}
          value={null}
          filterOptions={(opts, state) =>
            filterJumpToColumnOptions(opts, state.inputValue)
          }
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.field === value.field}
          noOptionsText="No matching columns"
          onChange={(_event, option) => {
            if (option) handleSelect(option);
          }}
          onClose={(_event, reason) => {
            // Autocomplete swallows Escape, so close the Popover from here
            if (reason === "escape") setOpen(false);
          }}
          slots={{ popper: InlinePopper }}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            const showField =
              option.label !== option.field && !option.field.startsWith("__");
            return (
              <li key={key} {...optionProps}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" component="span">
                    {option.label}
                  </Typography>
                  {option.hidden && (
                    <Typography
                      variant="caption"
                      component="span"
                      color="warning.main"
                      sx={{ ml: 0.5 }}
                    >
                      (hidden — will be shown)
                    </Typography>
                  )}
                  {option.pinned && (
                    <Typography
                      variant="caption"
                      component="span"
                      color="text.secondary"
                      sx={{ ml: 0.5 }}
                    >
                      (pinned)
                    </Typography>
                  )}
                  {showField && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      noWrap
                    >
                      {option.field}
                    </Typography>
                  )}
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              size="small"
              placeholder="Search columns..."
              inputProps={{
                ...params.inputProps,
                "aria-label": "Jump to column",
              }}
            />
          )}
        />
      </Popover>
    </>
  );
}
